// Adapted from react-phone-input-2 https://github.com/bl00mber/react-phone-input-2/blob/master/src/index.js

import { ChangeEvent, FocusEvent, RefObject, useEffect, useRef, useState } from 'react'

import { countriesAndTerritories, CountryTerritory } from '@/constants/countriesAndTerritories'

export type UsePhoneNumberProps = {
  defaultValue?: string
  onChange?: (value: string) => void
  placeholder?: string
}

export type UsePhoneNumberReturn = {
  formattedNumber: string
  selectedCountry: CountryTerritory
  placeholder: string
  inputRef: RefObject<HTMLInputElement | null>
  handleInput: (e: ChangeEvent<HTMLInputElement>) => void
  handleInputFocus: (e: FocusEvent<HTMLInputElement>) => void
  setCountry: (country: CountryTerritory) => void
}

const PREFIX = '+'

export const usePhoneNumber = (props: UsePhoneNumberProps): UsePhoneNumberReturn => {
  const [formattedNumber, setFormattedNumber] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<CountryTerritory>(
    // Default to Great Britain
    countriesAndTerritories.find((c) => c.iso2 === 'gb') || countriesAndTerritories[0]
  )
  const [freezeSelection, setFreezeSelection] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Function to detect country based on dial code
  const guessSelectedCountry = (inputNumber: string): CountryTerritory => {
    const secondBestGuess = countriesAndTerritories.find((o) => o.iso2 === 'gb') || selectedCountry
    if (inputNumber.trim() === '') return secondBestGuess

    const bestGuess = countriesAndTerritories.reduce(
      (selectedCountry, country) => {
        if (inputNumber.startsWith(country.dialCode)) {
          if (country.dialCode.length > selectedCountry.dialCode.length) {
            return country
          }
          if (
            country.dialCode.length === selectedCountry.dialCode.length &&
            (country.priority || 0) < (selectedCountry.priority || 10001)
          ) {
            return country
          }
        }
        return selectedCountry
      },
      { dialCode: '', priority: 10001 } as CountryTerritory
    )

    if (!bestGuess.name) return secondBestGuess
    return bestGuess
  }

  // Format number based on country pattern
  const formatNumber = (text: string, country: CountryTerritory): string => {
    if (!country) return text

    const { format } = country
    const autoFormat = true

    if (!text || text.length === 0) {
      return PREFIX
    }

    // for all strings with length less than 2, just return it (1, 2 etc.)
    // also return the same text if the selected country has no fixed format
    if ((text && text.length < 2) || !format || !autoFormat) {
      return PREFIX + text
    }

    // The format pattern should work on the number WITHOUT the dial code
    // First, remove the dial code from the input number
    let numberWithoutDialCode = text
    if (text.startsWith(country.dialCode)) {
      numberWithoutDialCode = text.substring(country.dialCode.length)
    }

    if (numberWithoutDialCode.length === 0) {
      // Only dial code entered, show: +44
      return PREFIX + country.dialCode
    }

    // Apply format pattern to the number part (without dial code)
    const formattedObject = format.split('').reduce(
      (acc, character) => {
        if (acc.remainingText.length === 0) {
          return acc
        }

        if (character !== '.') {
          return {
            formattedText: acc.formattedText + character,
            remainingText: acc.remainingText
          }
        }

        const [head, ...tail] = acc.remainingText

        return {
          formattedText: acc.formattedText + head,
          remainingText: tail
        }
      },
      {
        formattedText: '',
        remainingText: numberWithoutDialCode.split('')
      }
    )

    let formattedNumber = formattedObject.formattedText + formattedObject.remainingText.join('')

    // Always close brackets
    if (formattedNumber.includes('(') && !formattedNumber.includes(')')) {
      formattedNumber += ')'
    }

    // Return: +{dialCode} {formattedNumber}
    return PREFIX + country.dialCode + (formattedNumber ? ' ' + formattedNumber : '')
  }

  // Put the cursor to the end of the input
  const cursorToEnd = () => {
    const input = inputRef.current
    if (!input || document.activeElement !== input) return
    input.focus()
    let len = input.value.length
    if (input.value.charAt(len - 1) === ')') len = len - 1
    input.setSelectionRange(len, len)
  }

  // Get maximum allowed digits for a country format
  const getMaxDigitsForCountry = (country: CountryTerritory): number => {
    if (!country.format) return 15 // Default limit if no format
    // Count dots in format pattern - each dot represents one digit
    return (country.format.match(/\./g) || []).length
  }

  // Handle input changes
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    let newFormattedNumber = PREFIX
    let newSelectedCountry = selectedCountry
    let newFreezeSelection = freezeSelection

    if (value === PREFIX) {
      // we should handle change when we delete the last digit
      if (props.onChange) props.onChange('')
      setFormattedNumber('')
      return
    }

    // Does exceed default 15 digit phone number limit
    if (value.replace(/\D/g, '').length > 15) {
      return
    }

    // if the input is the same as before, must be some special key like enter etc.
    if (value === formattedNumber) return

    if (value.length > 0) {
      // before entering the number in new format, lets check if the dial code now matches some other country
      const inputNumber = value.replace(/\D/g, '')

      // we don't need to send the whole number to guess the country... only the first 6 characters are enough
      // the guess country function can then use memoization much more effectively since the set of input it
      // gets has drastically reduced
      if (
        !freezeSelection ||
        (!!selectedCountry && selectedCountry.dialCode.length > inputNumber.length)
      ) {
        newSelectedCountry = guessSelectedCountry(inputNumber.substring(0, 6)) || selectedCountry
        newFreezeSelection = false
      }

      // Check if we exceed the maximum digits allowed for this country
      const maxDigits = getMaxDigitsForCountry(newSelectedCountry)
      const dialCodeLength = newSelectedCountry.dialCode.length
      const numberPart = inputNumber.substring(dialCodeLength)

      // If the number part exceeds the maximum allowed digits, truncate it
      if (numberPart.length > maxDigits) {
        const truncatedNumber = inputNumber.substring(0, dialCodeLength + maxDigits)
        newFormattedNumber = formatNumber(truncatedNumber, newSelectedCountry)
      } else {
        newFormattedNumber = formatNumber(inputNumber, newSelectedCountry)
      }

      newSelectedCountry = newSelectedCountry.dialCode ? newSelectedCountry : selectedCountry
    }

    const oldCaretPosition = e.target.selectionStart || 0
    let caretPosition = e.target.selectionStart || 0
    const oldFormattedText = formattedNumber
    const diff = newFormattedNumber.length - oldFormattedText.length

    setFormattedNumber(newFormattedNumber)
    setFreezeSelection(newFreezeSelection)
    setSelectedCountry(newSelectedCountry)

    // Handle cursor positioning exactly like react-phone-input-2
    setTimeout(() => {
      if (!inputRef.current) return

      if (diff > 0) {
        caretPosition = caretPosition - diff
      }

      const lastChar = newFormattedNumber.charAt(newFormattedNumber.length - 1)

      if (lastChar === ')') {
        inputRef.current.setSelectionRange(
          newFormattedNumber.length - 1,
          newFormattedNumber.length - 1
        )
      } else if (caretPosition > 0 && oldFormattedText.length >= newFormattedNumber.length) {
        inputRef.current.setSelectionRange(caretPosition, caretPosition)
      } else if (oldCaretPosition < oldFormattedText.length) {
        inputRef.current.setSelectionRange(oldCaretPosition, oldCaretPosition)
      }
    }, 0)

    if (props.onChange) {
      props.onChange(newFormattedNumber.replace(/[^0-9]+/g, ''))
    }
  }

  // Handle input focus
  const handleInputFocus = (_event: FocusEvent<HTMLInputElement>) => {
    // if the input is blank or just has prefix, insert dial code of the selected country
    if (inputRef.current) {
      if ((inputRef.current.value === PREFIX || inputRef.current.value === '') && selectedCountry) {
        const newFormattedNumber = PREFIX + selectedCountry.dialCode
        setFormattedNumber(newFormattedNumber)
        setTimeout(cursorToEnd, 0)
      }
    }
  }

  // Generate placeholder based on detected country format
  const getPlaceholder = () => {
    if (selectedCountry && selectedCountry.format) {
      return `+${selectedCountry.dialCode} ${selectedCountry.format.replaceAll('.', '0')}`
    }
    return props.placeholder || '+44 20 7946 0958'
  }

  // Initialize with default value and GB dial code
  useEffect(() => {
    if (props.defaultValue) {
      const inputNumber = props.defaultValue.replace(/\D/g, '')
      const country = guessSelectedCountry(inputNumber.substring(0, 6))
      setSelectedCountry(country)
      const formatted = formatNumber(inputNumber, country)
      setFormattedNumber(formatted)
    } else {
      // Initialize with the default country's dial code (GB: +44)
      const defaultCountry =
        countriesAndTerritories.find((c) => c.iso2 === 'gb') || countriesAndTerritories[0]
      setFormattedNumber(PREFIX + defaultCountry.dialCode)
    }
  }, [props.defaultValue])

  // Handle manual country selection
  const setCountry = (country: CountryTerritory) => {
    setSelectedCountry(country)
    setFreezeSelection(true)
    // Update the formatted number with the new country's dial code
    const newFormattedNumber = PREFIX + country.dialCode
    setFormattedNumber(newFormattedNumber)

    // Focus the input and position cursor at the end
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
        cursorToEnd()
      }
    }, 0)

    // Notify parent component of the change
    if (props.onChange) {
      props.onChange(country.dialCode)
    }
  }

  return {
    formattedNumber,
    selectedCountry,
    placeholder: getPlaceholder(),
    inputRef,
    handleInput,
    handleInputFocus,
    setCountry
  }
}
